import {Phase2Api} from "../api";
import {useApolloClient} from "react-apollo";
import {RemovePaxCgoFromFlightMutation} from "./graphql";
import useApolloClientHelper from "../../../../../hooks/useApolloClientHelper";
import {ScheduledFlightFragment} from "../../../../../Queries/Scheduling";
import {arrayToMapAccumulate} from "../../../../../common/util-ts";
import {useContext} from "react";
import {OrgDataContext} from "../../../../../context/orgData";
import {isFlightScheduled} from "../util";
import {FlightNode} from "../../../../../__generated__/graphql/types";
import {getSNodeID} from "../../../../../components/scheduling/util";

export function usePurgeFromFlight(api: Phase2Api){
    const apolloClient = useApolloClient();
    const apolloHelper = useApolloClientHelper();
    const orgData = useContext(OrgDataContext);

    const dispatch = api.getDispatch();

    return async function start(){

        dispatch({ type: 'PURGE_TOOL.SET_PURGE_STATUS', payload: { status: 'LOADING' } });

        try {
            const flightIDList = getFlightIDList(api);

            const flights = await apolloHelper.resolveEntityIds<FlightNode>({
                ids: flightIDList,
                typename: 'FlightNode',
                fragment: ScheduledFlightFragment,
                fragmentName: 'ScheduledFlightFragment',
                fetchPolicy: 'network-only'
            });

            // Flights that are not scheduled or uninitalized should not be modified.
            const ineligibleFlights = flights
                .filter((flight) => {
                    return !isFlightScheduled(flight)
                })
                .map((f) => f._id);

            const entitiesOnFlight = getSelectedEntities(api)
                .filter((entity) => entity.currentCarrierID?._id);

            const flightsWithEntities = arrayToMapAccumulate(
                entitiesOnFlight,
                (entity) => entity.currentCarrierID?._id
            );

            const flightsUpdating = Array.from(flightsWithEntities.entries())
                // Exclude ineligble flights
                .filter(([flightId]) => !ineligibleFlights.includes(flightId))
                .map(([flightId, entities]) => {
                return apolloClient.mutate({
                    mutation: RemovePaxCgoFromFlightMutation,
                    variables: {
                        flightID: flightId,
                        paxIDs: entities
                            .filter((e) => e.classType === 'flytsuite.paxnode')
                            .map((e) => e._id),
                        cgoIDs: entities
                            .filter((e) => e.classType === 'flytsuite.cgonode')
                            .map((e) => e._id)
                    }
                });
            })

            await Promise.all(flightsUpdating);

            dispatch({ type: 'PURGE_TOOL.SET_PURGE_STATUS', payload: { status: 'SUCCESS' } });
        }
        catch (error){
            console.error('Failed to purge pax/cgo from flight:', error);
            dispatch({ type: 'PURGE_TOOL.SET_PURGE_STATUS', payload: { status: 'ERROR' , error: error } });
            throw error;
        }

    }
}

function getSelectedEntities(api: Phase2Api){
    return api.data.originalEntities.get()
        .filter((entity) => api.purgeEntitiesFromFlightTool.selectedEntityIds.includes(getSNodeID(api.toScheduleNode(entity))))
}

function getFlightIDList(api: Phase2Api){
    const carrierIDList = new Set<string>();
    getSelectedEntities(api)
        .filter((entity) => entity.currentCarrierID?._id && String(entity.currentCarrierID._id).startsWith('FLT-'))
        .forEach((entity) => carrierIDList.add(entity.currentCarrierID._id));

    return Array.from(carrierIDList);
}